import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Phone, CalendarCheck, UserPlus } from "lucide-react";
import { site } from "@/lib/site";
import { supabase } from "@/integrations/supabase/client";

/**
 * Sticky bottom CTA shown on mobile only. Hidden inside the authenticated
 * portal and on the booking page itself to avoid redundancy.
 *
 * Signed-out visitors also get a one-tap "Sign up" that deep-links to
 * /auth?mode=signup&redirect=<current path> so they land back exactly where
 * they were after creating the account.
 */
export function StickyMobileCTA() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setSignedIn(Boolean(data.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(Boolean(session));
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (pathname.startsWith("/portal") || pathname.startsWith("/auth") || pathname === "/book") {
    return null;
  }

  const showSignup = signedIn === false;
  // Preserve the page (and its query string) the visitor came from.
  const returnTo = `${pathname}${searchStr ?? ""}`;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className={`grid ${showSignup ? "grid-cols-4" : "grid-cols-3"} divide-x divide-border`}
      >
        <a
          href={`tel:${site.phoneIntl}`}
          className="flex min-h-[56px] flex-col items-center justify-center gap-1 py-3 text-sm font-medium text-navy active:bg-muted"
          aria-label={`Call Nkadimeng Danny Legal on ${site.phone}`}
        >
          <Phone className="h-5 w-5 text-gold" aria-hidden="true" />
          Call
        </a>
        <a
          href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent(
            "Hello, I'd like to enquire about your legal services.",
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-[56px] flex-col items-center justify-center gap-1 py-3 text-sm font-semibold text-[#075E54] active:bg-[#25D366]/10"
          aria-label="Chat with Nkadimeng Danny Legal on WhatsApp (opens in new tab)"
        >
          WhatsApp
        </a>
        {showSignup && (
          <Link
            to="/auth"
            search={{ mode: "signup", redirect: returnTo }}
            className="flex min-h-[56px] flex-col items-center justify-center gap-1 py-3 text-sm font-semibold text-navy active:bg-muted"
            aria-label="Create a free account and return to this page"
          >
            <UserPlus className="h-5 w-5 text-navy" aria-hidden="true" />
            Sign up
          </Link>
        )}
        <Link
          to="/book"
          className="flex min-h-[56px] flex-col items-center justify-center gap-1 bg-gold py-3 text-sm font-semibold text-gold-foreground active:bg-gold/90"
          aria-label="Book a consultation"
        >
          <CalendarCheck className="h-5 w-5" aria-hidden="true" />
          Book
        </Link>
      </div>
    </div>
  );
}
